/**
 * How many tasks are waiting for this reader **in the scope on screen** — one rule, two readers
 * (mirrors `decisionsWaitingInScope.ts`, I-0900).
 *
 * The sidebar badge and the page both show this number. They are in different components, and the
 * only way they cannot disagree is to ask one function rather than each restating the same check
 * against `TasksStore`.
 */

/** What `TasksStore` has to be able to answer for this rule; the store itself satisfies it. */
export interface ITasksSetSource {
  allWorkAreas: boolean;
  workAreaId: string | null;
  /** The namespace the current set is scoped to (F-0383, §10.9), or `null` outside that scope. */
  namespaceId?: string | null;
  taskCount: number | undefined;
  /** I-2602 (TF-38): how many of the set are urgent; absent where a reader keeps no such count. */
  urgentCount?: number | undefined;
  unknown: boolean;
}

/** The scope the screen is showing, which the set has to be about for its number to mean anything. */
export interface ITasksScope {
  allWorkAreas: boolean;
  workAreaId: string | null;
  /** The namespace on screen (F-0383, §10.9), or `null`/absent outside that scope. */
  namespaceId?: string | null;
}

/**
 * Whether the set the store is holding is the set this screen is about.
 *
 * ⛔ `workAreaId === scope.workAreaId` alone is not enough: both are `null` under All work areas and
 * before the first read, so a narrow scope with no work area yet would adopt the All-work-areas
 * count. A namespace scope is its own third case, the same reason `decisionsSetMatchesScope` has one.
 */
export function tasksSetMatchesScope(tasks: ITasksSetSource, scope: ITasksScope): boolean {
  if (scope.allWorkAreas) return tasks.allWorkAreas;
  if (scope.namespaceId) return tasks.namespaceId === scope.namespaceId;
  return scope.workAreaId !== null && tasks.workAreaId === scope.workAreaId;
}

/** The number to show, or `undefined` when it is not known for this scope yet. */
export function tasksWaitingInScope(
  tasks: ITasksSetSource,
  scope: ITasksScope,
): number | undefined {
  return tasksSetMatchesScope(tasks, scope) ? tasks.taskCount : undefined;
}

/** I-2602 (TF-38): the count's urgent part for this scope, by the same rule as the count. */
export function tasksUrgentInScope(tasks: ITasksSetSource, scope: ITasksScope): number | undefined {
  return tasksSetMatchesScope(tasks, scope) ? tasks.urgentCount : undefined;
}

/** The read for this scope failed: say nothing, rather than `...` for a number never coming. */
export function tasksWaitingUnknownInScope(tasks: ITasksSetSource, scope: ITasksScope): boolean {
  return tasksSetMatchesScope(tasks, scope) && tasks.unknown;
}
